import { ActivatedRouteSnapshot, ParamMap } from '@angular/router';
import { parseApiErrorBody } from './api-error.utils';

/** BE may send InviteId or inviteId in external vendor/payment links. */
export function getInviteIdFromQueryParams(
  queryParams: Record<string, unknown>,
  queryParamMap?: ParamMap
): string | null {
  const fromObject =
    (queryParams['InviteId'] as string | undefined) ??
    (queryParams['inviteId'] as string | undefined);

  if (fromObject) {
    return fromObject;
  }

  return (
    queryParamMap?.get('InviteId') ??
    queryParamMap?.get('inviteId') ??
    null
  );
}

export function getInviteIdFromRoute(route: ActivatedRouteSnapshot): string | null {
  let current: ActivatedRouteSnapshot | null = route;

  while (current) {
    const inviteId = getInviteIdFromQueryParams(
      current.queryParams,
      current.queryParamMap
    );
    if (inviteId) {
      return inviteId;
    }
    current = current.firstChild ?? null;
  }

  return null;
}

export function getInviteIdFromUrl(url: string): string | null {
  try {
    const query = url.includes('?') ? url.split('?')[1].split('#')[0] : '';
    if (!query) {
      return null;
    }

    const params = new URLSearchParams(query);
    return params.get('InviteId') ?? params.get('inviteId');
  } catch {
    return null;
  }
}

export function resolveInviteId(
  route: ActivatedRouteSnapshot,
  url?: string
): string | null {
  return (
    (url ? getInviteIdFromUrl(url) : null) ??
    getInviteIdFromRoute(route)
  );
}

/** Same path check pattern used by external vendor create invite links. */
export function isVendorCreateRoute(url: string): boolean {
  return (
    (url.includes('/vendor') || url.includes('/vendors')) &&
    url.includes('/create')
  );
}

export function isVendorPaymentDetailsRoute(url: string): boolean {
  return (
    (url.includes('/vendor') || url.includes('/vendors')) &&
    url.includes('/payment-details')
  );
}

export function isVendorExternalInviteRoute(url: string): boolean {
  return isVendorCreateRoute(url) || isVendorPaymentDetailsRoute(url);
}

/** Query param used on /vendors/create as fallback external payment link. */
export const EXTERNAL_PAYMENT_FLOW_QUERY = 'payment-details';

export function getExternalPaymentFlow(
  queryParams: Record<string, unknown>,
  queryParamMap?: ParamMap
): string | null {
  const fromObject = queryParams['externalFlow'] as string | undefined;
  if (fromObject) {
    return fromObject;
  }
  return queryParamMap?.get('externalFlow') ?? null;
}

export type InviteLinkFailureReason = 'expired' | 'alreadyUsed';

/** @deprecated Use InviteLinkFailureReason */
export type PaymentDetailLinkFailureReason = InviteLinkFailureReason;

/** Maps invite verify API errors to the correct external status screen. */
export function resolveInviteLinkVerifyFailure(
  error: unknown
): InviteLinkFailureReason {
  const parsed = parseApiErrorBody(error);
  const title = String(parsed?.['title'] ?? '').toLowerCase();
  const message = String(parsed?.['message'] ?? '').toLowerCase();

  if (
    title.includes('alreadyvisited') ||
    title.includes('invitealreadyvisited') ||
    message.includes('already visited') ||
    message.includes('already been used') ||
    message.includes('already used')
  ) {
    return 'alreadyUsed';
  }

  return 'expired';
}

/** @deprecated Use resolveInviteLinkVerifyFailure */
export const resolvePaymentDetailVerifyFailure = resolveInviteLinkVerifyFailure;

export function isExternalPaymentDetailsInvite(
  url: string,
  queryParams: Record<string, unknown>,
  queryParamMap?: ParamMap
): boolean {
  const inviteId = getInviteIdFromUrl(url);
  if (!inviteId) {
    return false;
  }

  if (isVendorPaymentDetailsRoute(url)) {
    return true;
  }

  return (
    isVendorCreateRoute(url) &&
    getExternalPaymentFlow(queryParams, queryParamMap) ===
      EXTERNAL_PAYMENT_FLOW_QUERY
  );
}
